
// =========================================================
// DEBUTS CROP
// CHECKOUT
// PREMIUM NEON / VAPORWAVE EDITION
// CART SYNC + DJANGO CHECKOUT
// =========================================================

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileImage,
  LockKeyhole,
  MapPin,
  Minus,
  Package,
  Plus,
  QrCode,
  ShieldCheck,
  ShoppingCart,
  Trash2,
  Truck,
  Upload,
  User,
  Store,
  Smartphone,
  Building2,
  Clock3,
  AlertCircle,
} from "lucide-react";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import "../../styles/checkout.css";

import bg from "../../assets/bg.png";
import logo from "../../assets/logo.png";

// =========================================================
// CONFIG
// =========================================================

const CART_KEY = "cart";
const AUTH_KEY = "auth";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.debutscrop.com/api"
).replace(/\/+$/, "");

// =========================================================
// HELPERS
// =========================================================

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const getQuantity = (item) => {
  const quantity = Number(
    item?.quantity ?? item?.qty ?? 1
  );

  return Number.isFinite(quantity) && quantity > 0
    ? Math.floor(quantity)
    : 1;
};

const getPrice = (item) =>
  Number(
    item?.price ??
      item?.sale_price ??
      item?.unit_price ??
      item?.product?.price ??
      item?.product_price ??
      0
  );

const getProductId = (item) =>
  item?.product_id ??
  item?.product?.id ??
  item?.product ??
  item?.id;

const getProductName = (item) =>
  item?.name ||
  item?.product_name ||
  item?.product?.name ||
  "Producto";

const getApiOrigin = () => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return "";
  }
};

const getProductImage = (item) => {
  const image =
    item?.image ||
    item?.image_url ||
    item?.product_image ||
    item?.product?.image ||
    item?.product?.image_url ||
    "";

  if (!image || typeof image !== "string") {
    return "";
  }

  if (/^https?:\/\//i.test(image)) {
    return image;
  }

  const origin = getApiOrigin();

  if (!origin) {
    return image;
  }

  if (image.startsWith("/")) {
    return `${origin}${image}`;
  }

  return `${origin}/media/${image}`;
};

const readCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((item) => item && typeof item === "object")
      .map((item) => {
        const quantity = getQuantity(item);

        return {
          ...item,
          qty: quantity,
          quantity,
        };
      });
  } catch {
    return [];
  }
};

const readAuth = () => {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// =========================================================
// API ERROR
// =========================================================

const getApiErrorMessage = async (response) => {
  try {
    const data = await response.json();

    if (typeof data === "string") {
      return data;
    }

    if (data?.detail) {
      return String(data.detail);
    }

    if (data?.message) {
      return String(data.message);
    }

    if (data?.error) {
      return String(data.error);
    }

    if (data?.product_ids) {
      return (
        `${data.detail || "Hay productos inválidos."} ` +
        `IDs: ${data.product_ids.join(", ")}`
      );
    }

    const entries = Object.entries(data || {});

    for (const [field, value] of entries) {
      if (Array.isArray(value) && value.length) {
        return `${field}: ${value[0]}`;
      }

      if (typeof value === "string") {
        return `${field}: ${value}`;
      }

      if (value && typeof value === "object") {
        const nested = Object.values(value)[0];

        if (Array.isArray(nested) && nested.length) {
          return `${field}: ${nested[0]}`;
        }

        if (typeof nested === "string") {
          return `${field}: ${nested}`;
        }
      }
    }

    return `No fue posible procesar la solicitud (${response.status}).`;
  } catch {
    return `No fue posible procesar la solicitud (${response.status}).`;
  }
};

// =========================================================
// CART SYNC
//
// POST /api/cart/sync/
//
// Django recibe la lista completa del carrito local,
// valida productos y cantidades, consulta los precios
// reales y reemplaza los artículos del carrito.
// =========================================================

const syncCartWithBackend = async (cartItems, token) => {
  if (!token) {
    throw new Error(
      "Tu sesión ha expirado. Inicia sesión nuevamente."
    );
  }

  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    throw new Error("Tu carrito está vacío.");
  }

  const items = cartItems.map((item, index) => {
    const rawId = getProductId(item);
    const productId = Number(rawId);
    const quantity = Number(getQuantity(item));

    if (
      rawId === undefined ||
      rawId === null ||
      rawId === "" ||
      !Number.isSafeInteger(productId) ||
      productId <= 0
    ) {
      throw new Error(
        `El producto ${getProductName(item)} (posición ${
          index + 1
        }) no tiene un ID válido.`
      );
    }

    if (
      !Number.isSafeInteger(quantity) ||
      quantity <= 0
    ) {
      throw new Error(
        `La cantidad de ${getProductName(item)} no es válida.`
      );
    }

    return {
      product_id: productId,
      quantity,
    };
  });

  const response = await fetch(
    `${API_BASE_URL}/cart/sync/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ items }),
    }
  );

  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response));
  }

  const data = await response.json();

  if (data?.success === false) {
    throw new Error(
      data?.detail ||
        data?.message ||
        "Django no pudo sincronizar el carrito."
    );
  }

  return data?.data ?? data;
};

// =========================================================
// COMPONENT
// =========================================================

export default function Checkout() {
  const navigate = useNavigate();

  // =======================================================
  // STATE
  // =======================================================

  const [cart, setCart] = useState([]);
  const [auth, setAuth] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [step, setStep] = useState(1);
  const [deliveryType, setDeliveryType] = useState("home");

  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loadingPaymentSettings, setLoadingPaymentSettings] =
    useState(false);

  const [placingOrder, setPlacingOrder] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);

  const [checkout, setCheckout] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);

  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    neighborhood: "",
    city: "",
    department: "",
    addressReference: "",
    pickupStoreName: "",
    pickupStoreAddress: "",
    notes: "",
    paymentMethod: "nequi",
  });

  // =======================================================
  // AUTH + CART
  // =======================================================

  useEffect(() => {
    const currentAuth = readAuth();

    setAuth(currentAuth);
    setAuthChecked(true);

    if (!currentAuth?.access) {
      localStorage.setItem(
        "redirectAfterLogin",
        "/checkout"
      );

      navigate("/login", {
        replace: true,
        state: {
          message:
            "Inicia sesión o regístrate para continuar con tu compra.",
        },
      });

      return;
    }

    const currentCart = readCart();

    setCart(currentCart);

    if (!currentCart.length) {
      navigate("/cart", { replace: true });
    }
  }, [navigate]);

  // =======================================================
  // LOAD USER DATA
  // =======================================================

  useEffect(() => {
    if (!auth) {
      return;
    }

    setForm((previous) => ({
      ...previous,

      name:
        previous.name ||
        auth?.user?.first_name ||
        auth?.user?.name ||
        auth?.name ||
        "",

      email:
        previous.email ||
        auth?.user?.email ||
        auth?.email ||
        "",

      phone:
        previous.phone ||
        auth?.user?.phone ||
        auth?.phone ||
        "",
    }));
  }, [auth]);

  // =======================================================
  // LOAD PAYMENT SETTINGS
  // =======================================================

  useEffect(() => {
    if (!auth?.access) {
      return;
    }

    let cancelled = false;

    const loadPaymentSettings = async () => {
      setLoadingPaymentSettings(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/checkout/payment-settings/`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${auth.access}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            await getApiErrorMessage(response)
          );
        }

        const responseData = await response.json();

        // Permite respuestas directas o envueltas en data.
        const data = responseData?.data ?? responseData;

        if (cancelled) {
          return;
        }

        setPaymentSettings(data);

        if (
          !data?.nequi?.enabled &&
          data?.bank_transfer?.enabled
        ) {
          setForm((previous) => ({
            ...previous,
            paymentMethod: "transfer",
          }));
        } else if (
          !data?.nequi?.enabled &&
          !data?.bank_transfer?.enabled
        ) {
          setForm((previous) => ({
            ...previous,
            paymentMethod:
              deliveryType === "store"
                ? "cash_store"
                : "",
          }));
        }
      } catch (error) {
        console.error("Payment settings:", error);

        if (!cancelled) {
          setPaymentSettings(null);
          setErrorMessage(
            "No fue posible cargar los métodos de pago. Intenta recargar la página."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingPaymentSettings(false);
        }
      }
    };

    loadPaymentSettings();

    return () => {
      cancelled = true;
    };
  }, [auth, deliveryType]);

  // =======================================================
  // CART TOTALS
  // =======================================================

  const totalItems = useMemo(
    () =>
      cart.reduce(
        (total, item) => total + getQuantity(item),
        0
      ),
    [cart]
  );

  const subtotal = useMemo(
    () =>
      cart.reduce(
        (total, item) =>
          total + getPrice(item) * getQuantity(item),
        0
      ),
    [cart]
  );

  // El costo de envío definitivo lo calcula Django.
  const shipping = Number(checkout?.shipping_cost ?? 0);

  const backendSubtotal = Number(
    checkout?.subtotal ?? subtotal
  );

  const total = Number(
    checkout?.total ??
      backendSubtotal +
        shipping -
        Number(checkout?.discount ?? 0)
  );

  // =======================================================
  // UPDATE CART
  // =======================================================

  const updateCart = (nextCart) => {
    if (checkout) {
      return;
    }

    const normalized = nextCart.map((item) => {
      const quantity = getQuantity(item);

      return {
        ...item,
        qty: quantity,
        quantity,
      };
    });

    setCart(normalized);

    localStorage.setItem(
      CART_KEY,
      JSON.stringify(normalized)
    );

    window.dispatchEvent(new Event("cartUpdated"));

    if (!normalized.length) {
      navigate("/cart", { replace: true });
    }
  };

  // =======================================================
  // CHANGE QUANTITY
  // =======================================================

  const changeQuantity = (item, delta) => {
    if (checkout) {
      return;
    }

    const id = getProductId(item);

    const next = cart.map((current) => {
      if (
        String(getProductId(current)) !== String(id)
      ) {
        return current;
      }

      const rawStock =
        current?.stock ??
        current?.product?.stock;

      const stock =
        rawStock === undefined ||
        rawStock === null ||
        rawStock === ""
          ? Number.MAX_SAFE_INTEGER
          : Math.max(0, Number(rawStock));

      const nextQuantity = Math.max(
        1,
        Math.min(
          getQuantity(current) + delta,
          stock
        )
      );

      return {
        ...current,
        qty: nextQuantity,
        quantity: nextQuantity,
      };
    });

    updateCart(next);
  };

  // =======================================================
  // REMOVE ITEM
  // =======================================================

  const removeItem = (item) => {
    if (checkout) {
      return;
    }

    const id = getProductId(item);

    updateCart(
      cart.filter(
        (current) =>
          String(getProductId(current)) !== String(id)
      )
    );
  };

  // =======================================================
  // FORM UPDATE
  // =======================================================

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrorMessage("");
  };

  // =======================================================
  // DELIVERY TYPE
  // =======================================================

  const selectDeliveryType = (type) => {
    setDeliveryType(type);
    setErrorMessage("");

    setForm((previous) => {
      let paymentMethod = previous.paymentMethod;

      if (
        type === "home" &&
        paymentMethod === "cash_store"
      ) {
        paymentMethod = paymentSettings?.nequi?.enabled
          ? "nequi"
          : paymentSettings?.bank_transfer?.enabled
            ? "transfer"
            : "";
      }

      if (
        type === "store" &&
        !paymentSettings?.nequi?.enabled &&
        !paymentSettings?.bank_transfer?.enabled
      ) {
        paymentMethod = "cash_store";
      }

      return {
        ...previous,
        paymentMethod,
      };
    });
  };

  // =======================================================
  // PAYMENT METHODS
  // =======================================================

  const paymentMethods = useMemo(() => {
    const methods = [];

    if (paymentSettings?.nequi?.enabled) {
      methods.push({
        value: "nequi",
        title: "Nequi",
        description:
          "Realiza el pago al número de Nequi indicado.",
        icon: Smartphone,
      });
    }

    if (paymentSettings?.bank_transfer?.enabled) {
      methods.push({
        value: "transfer",
        title: "Transferencia bancaria",
        description:
          "Realiza una transferencia a nuestra cuenta bancaria.",
        icon: Building2,
      });
    }

    if (deliveryType === "store") {
      methods.push({
        value: "cash_store",
        title: "Pago en tienda",
        description:
          "Paga directamente al recoger tu pedido en la tienda.",
        icon: Store,
      });
    }

    return methods;
  }, [paymentSettings, deliveryType]);

  const selectedPaymentMethod = form.paymentMethod;

  // =======================================================
  // VALIDATE STEP 1
  // =======================================================

  const continueToPayment = (event) => {
    event.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim()
    ) {
      setErrorMessage(
        "Completa nombre, correo y teléfono."
      );
      return;
    }

    if (deliveryType === "home") {
      if (
        !form.address.trim() ||
        !form.city.trim() ||
        !form.department.trim()
      ) {
        setErrorMessage(
          "Completa la dirección, ciudad y departamento para el domicilio."
        );
        return;
      }
    }

    if (deliveryType === "store") {
      if (!form.pickupStoreName.trim()) {
        setErrorMessage(
          "Selecciona la tienda donde deseas recoger tu pedido."
        );
        return;
      }
    }

    if (!cart.length) {
      navigate("/cart", { replace: true });
      return;
    }

    setStep(2);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================================================
  // CREATE CHECKOUT
  //
  // 1. Validar sesión y método de pago.
  // 2. Sincronizar carrito local con Django.
  // 3. Crear checkout desde el carrito persistente.
  // 4. Limpiar localStorage solo si el checkout existe.
  // =======================================================

  const createCheckout = async () => {
    if (placingOrder || checkout) {
      return;
    }

    if (!auth?.access) {
      setErrorMessage(
        "Tu sesión ha expirado. Inicia sesión nuevamente."
      );
      return;
    }

    if (!cart.length) {
      setErrorMessage("Tu carrito está vacío.");
      return;
    }

    if (loadingPaymentSettings) {
      setErrorMessage(
        "Espera mientras se cargan los métodos de pago."
      );
      return;
    }

    const selectedMethodIsAvailable =
      paymentMethods.some(
        (method) => method.value === form.paymentMethod
      );

    if (!selectedMethodIsAvailable) {
      setErrorMessage(
        "Selecciona un método de pago disponible."
      );
      return;
    }

    setPlacingOrder(true);
    setErrorMessage("");
    setSuccessMessage("");

    const checkoutPayload = {
      delivery_type: deliveryType,

      customer_name: form.name.trim(),
      customer_phone: form.phone.trim(),
      customer_email: form.email.trim(),

      address:
        deliveryType === "home"
          ? form.address.trim()
          : "",

      neighborhood:
        deliveryType === "home"
          ? form.neighborhood.trim()
          : "",

      city:
        deliveryType === "home"
          ? form.city.trim()
          : "",

      department:
        deliveryType === "home"
          ? form.department.trim()
          : "",

      address_reference:
        deliveryType === "home"
          ? form.addressReference.trim()
          : "",

      pickup_store_name:
        deliveryType === "store"
          ? form.pickupStoreName.trim()
          : "",

      pickup_store_address:
        deliveryType === "store"
          ? form.pickupStoreAddress.trim()
          : "",

      payment_method: form.paymentMethod,
      customer_notes: form.notes.trim(),
    };

    try {
      // ---------------------------------------------------
      // STEP 1: SYNC CART WITH DJANGO
      // ---------------------------------------------------

      await syncCartWithBackend(cart, auth.access);

      // ---------------------------------------------------
      // STEP 2: CREATE CHECKOUT
      //
      // No enviamos precios ni totales del frontend.
      // Django debe calcularlos con su propia base de datos.
      // ---------------------------------------------------

      const response = await fetch(
        `${API_BASE_URL}/checkout/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${auth.access}`,
          },
          body: JSON.stringify(checkoutPayload),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getApiErrorMessage(response)
        );
      }

      const responseData = await response.json();

      // Acepta tanto una respuesta directa como una envuelta.
      const createdCheckout =
        responseData?.data ?? responseData?.checkout ?? responseData;

      if (
        !createdCheckout ||
        typeof createdCheckout !== "object" ||
        !createdCheckout.id
      ) {
        throw new Error(
          "El servidor no confirmó la creación del checkout. Tu carrito local se ha conservado."
        );
      }

      setCheckout(createdCheckout);

      const generatedNumber =
        createdCheckout.checkout_number ||
        createdCheckout.number ||
        createdCheckout.id;

      setOrderNumber(String(generatedNumber));

      // ---------------------------------------------------
      // STEP 3: CLEAR LOCAL CART AFTER SUCCESS
      // ---------------------------------------------------

      localStorage.removeItem(CART_KEY);
      setCart([]);

      window.dispatchEvent(new Event("cartUpdated"));

      // ---------------------------------------------------
      // STEP 4: PAYMENT FLOW
      // ---------------------------------------------------

      if (form.paymentMethod === "cash_store") {
        setOrderComplete(true);
      } else {
        setStep(3);
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Create checkout:", error);

      setErrorMessage(
        error?.message ||
          "No fue posible crear el checkout."
      );

      // El carrito local se conserva si falla la operación.
    } finally {
      setPlacingOrder(false);
    }
  };

  // =======================================================
  // UPLOAD PAYMENT PROOF
  // =======================================================

  const uploadPaymentProof = async () => {
    if (
      !checkout?.id ||
      !paymentProof ||
      uploadingProof
    ) {
      return;
    }

    if (!auth?.access) {
      setErrorMessage(
        "Tu sesión ha expirado. Inicia sesión nuevamente."
      );
      return;
    }

    if (
      !/^image\/(jpeg|png|webp)$/i.test(paymentProof.type)
    ) {
      setErrorMessage(
        "El comprobante debe ser una imagen JPG, PNG o WEBP."
      );
      return;
    }

    if (paymentProof.size > 10 * 1024 * 1024) {
      setErrorMessage(
        "El comprobante supera el límite de 10 MB."
      );
      return;
    }

    setUploadingProof(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const formData = new FormData();

      formData.append("payment_proof", paymentProof);

      const response = await fetch(
        `${API_BASE_URL}/checkout/${checkout.id}/payment-proof/`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${auth.access}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(
          await getApiErrorMessage(response)
        );
      }

      const responseData = await response.json();

      const updatedCheckout =
        responseData?.checkout ||
        responseData?.data ||
        responseData;

      if (
        updatedCheckout &&
        typeof updatedCheckout === "object" &&
        updatedCheckout.id
      ) {
        setCheckout(updatedCheckout);
      }

      setPaymentProof(null);

      setSuccessMessage(
        "Comprobante enviado correctamente. Tu pago quedó en revisión."
      );

      setStep(4);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Payment proof:", error);

      setErrorMessage(
        error?.message ||
          "No fue posible enviar el comprobante."
      );
    } finally {
      setUploadingProof(false);
    }
  };

  // =======================================================
  // SUCCESS: CASH IN STORE
  // =======================================================

  if (orderComplete) {
    return (
      <div
        className="checkout-page checkout-success-page"
        style={{ "--checkout-bg": `url(${bg})` }}
      >
        <Navbar />

        <main className="checkout-success">
          <div className="checkout-success-card">
            <div className="checkout-success-icon">
              <CheckCircle2 size={72} />
            </div>

            <span className="checkout-kicker">
              DEBUTS CROP · CHECKOUT
            </span>

            <h1>¡CHECKOUT CREADO!</h1>

            <p>
              Tu solicitud de compra fue registrada
              correctamente.
            </p>

            <div className="checkout-order-number">
              <span>NÚMERO DE CHECKOUT</span>
              <strong>{orderNumber}</strong>
            </div>

            {checkout?.status_display && (
              <div className="checkout-status-box">
                <Clock3 size={18} />
                <span>{checkout.status_display}</span>
              </div>
            )}

            <div className="checkout-success-actions">
              <button
                type="button"
                onClick={() => navigate("/catalog")}
              >
                SEGUIR COMPRANDO
                <ArrowRight size={18} />
              </button>

              <button
                type="button"
                className="secondary"
                onClick={() => navigate("/profile")}
              >
                VER MI PERFIL
              </button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // =======================================================
  // MAIN
  // =======================================================

  if (!authChecked) {
    return (
      <div
        className="checkout-page"
        style={{ "--checkout-bg": `url(${bg})` }}
      >
        <Navbar />
        <main className="checkout-main">
          <div className="checkout-container">
            <div className="checkout-card">
              <p>Verificando tu sesión...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="checkout-page"
      style={{ "--checkout-bg": `url(${bg})` }}
    >
      <Navbar />

      <main className="checkout-main">
        {/* HERO */}

        <section className="checkout-hero">
          <div className="checkout-hero-overlay" />

          <div className="checkout-hero-content">
            <img
              src={logo}
              alt="Debuts Crop"
              className="checkout-logo"
            />

            <span>DEBUTS CROP · CHECKOUT</span>

            <h1>FINALIZA TU COMPRA</h1>

            <p>
              Completa tus datos, selecciona cómo quieres
              recibir tu pedido y realiza el pago.
            </p>
          </div>
        </section>

        <div className="checkout-container">
          {/* BREADCRUMB */}

          <div className="checkout-breadcrumb">
            <button
              type="button"
              onClick={() => navigate("/cart")}
            >
              <ArrowLeft size={17} />
              Volver al carrito
            </button>

            <ChevronRight size={16} />
            <span>Checkout</span>
          </div>

          {/* STEPS */}

          <div className="checkout-steps">
            <div
              className={`checkout-step ${
                step >= 1 ? "active" : ""
              }`}
            >
              <span>01</span>
              <div>
                <strong>ENTREGA</strong>
                <small>Datos y recepción</small>
              </div>
            </div>

            <div className="checkout-step-line" />

            <div
              className={`checkout-step ${
                step >= 2 ? "active" : ""
              }`}
            >
              <span>02</span>
              <div>
                <strong>PAGO</strong>
                <small>Método de pago</small>
              </div>
            </div>

            <div className="checkout-step-line" />

            <div
              className={`checkout-step ${
                step >= 3 ? "active" : ""
              }`}
            >
              <span>03</span>
              <div>
                <strong>COMPROBANTE</strong>
                <small>Verificación</small>
              </div>
            </div>
          </div>

          {/* GLOBAL ERROR */}

          {errorMessage && (
            <div className="checkout-alert checkout-alert-error">
              <AlertCircle size={19} />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="checkout-alert checkout-alert-success">
              <CheckCircle2 size={19} />
              <span>{successMessage}</span>
            </div>
          )}

          {/* LAYOUT */}

          <div className="checkout-layout">
            <section className="checkout-form-area">
              {/* ===========================================
                  STEP 1: DELIVERY
              =========================================== */}

              {step === 1 && (
                <form
                  className="checkout-card"
                  onSubmit={continueToPayment}
                >
                  <div className="checkout-card-header">
                    <div className="checkout-card-title">
                      <div className="checkout-title-icon">
                        <Truck size={20} />
                      </div>

                      <div>
                        <span>01 · ENTREGA</span>
                        <h2>
                          ¿Cómo quieres recibir tu pedido?
                        </h2>
                      </div>
                    </div>

                    <ShieldCheck size={22} />
                  </div>

                  {/* DELIVERY OPTIONS */}

                  <div className="checkout-delivery-options">
                    <button
                      type="button"
                      className={`checkout-delivery-option ${
                        deliveryType === "home" ? "active" : ""
                      }`}
                      onClick={() => selectDeliveryType("home")}
                    >
                      <div className="checkout-delivery-icon">
                        <Truck size={27} />
                      </div>

                      <div>
                        <strong>DOMICILIO</strong>
                        <span>
                          Recibe tu pedido en la dirección
                          indicada.
                        </span>
                      </div>

                      {deliveryType === "home" && (
                        <CheckCircle2 size={20} />
                      )}
                    </button>

                    <button
                      type="button"
                      className={`checkout-delivery-option ${
                        deliveryType === "store" ? "active" : ""
                      }`}
                      onClick={() => selectDeliveryType("store")}
                    >
                      <div className="checkout-delivery-icon">
                        <Store size={27} />
                      </div>

                      <div>
                        <strong>RECOGER EN TIENDA</strong>
                        <span>
                          Recoge tu pedido directamente en
                          nuestra tienda.
                        </span>
                      </div>

                      {deliveryType === "store" && (
                        <CheckCircle2 size={20} />
                      )}
                    </button>
                  </div>

                  {/* CUSTOMER DATA */}

                  <div className="checkout-card-subsection">
                    <div className="checkout-subsection-title">
                      <User size={18} />
                      <span>INFORMACIÓN DEL CLIENTE</span>
                    </div>

                    <div className="checkout-form-grid">
                      <label>
                        <span>Nombre completo *</span>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={updateField}
                          placeholder="Tu nombre completo"
                          autoComplete="name"
                          required
                        />
                      </label>

                      <label>
                        <span>Correo electrónico *</span>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={updateField}
                          placeholder="correo@ejemplo.com"
                          autoComplete="email"
                          required
                        />
                      </label>

                      <label>
                        <span>Teléfono *</span>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={updateField}
                          placeholder="+57 300 000 0000"
                          autoComplete="tel"
                          required
                        />
                      </label>
                    </div>
                  </div>

                  {/* HOME ADDRESS */}

                  {deliveryType === "home" && (
                    <div className="checkout-card-subsection">
                      <div className="checkout-subsection-title">
                        <MapPin size={18} />
                        <span>DIRECCIÓN DE ENTREGA</span>
                      </div>

                      <div className="checkout-form-grid">
                        <label className="full-width">
                          <span>Dirección *</span>

                          <div className="checkout-input-icon">
                            <MapPin size={17} />
                            <input
                              type="text"
                              name="address"
                              value={form.address}
                              onChange={updateField}
                              placeholder="Calle, carrera, número, apartamento..."
                              autoComplete="street-address"
                              required
                            />
                          </div>
                        </label>

                        <label>
                          <span>Barrio</span>
                          <input
                            type="text"
                            name="neighborhood"
                            value={form.neighborhood}
                            onChange={updateField}
                            placeholder="Ej. El Poblado"
                          />
                        </label>

                        <label>
                          <span>Ciudad *</span>
                          <input
                            type="text"
                            name="city"
                            value={form.city}
                            onChange={updateField}
                            placeholder="Medellín"
                            autoComplete="address-level2"
                            required
                          />
                        </label>

                        <label>
                          <span>Departamento *</span>
                          <input
                            type="text"
                            name="department"
                            value={form.department}
                            onChange={updateField}
                            placeholder="Antioquia"
                            autoComplete="address-level1"
                            required
                          />
                        </label>

                        <label className="full-width">
                          <span>Referencia de dirección</span>
                          <textarea
                            name="addressReference"
                            value={form.addressReference}
                            onChange={updateField}
                            placeholder="Apartamento, torre, portería, punto de referencia..."
                            rows="3"
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {/* STORE PICKUP */}

                  {deliveryType === "store" && (
                    <div className="checkout-card-subsection">
                      <div className="checkout-subsection-title">
                        <Store size={18} />
                        <span>RECOGIDA EN TIENDA</span>
                      </div>

                      <div className="checkout-form-grid">
                        <label className="full-width">
                          <span>Tienda *</span>

                          <select
                            name="pickupStoreName"
                            value={form.pickupStoreName}
                            onChange={updateField}
                            required
                          >
                            <option value="">
                              Selecciona una tienda
                            </option>

                            <option value="Debuts Crop - El Poblado">
                              Debuts Crop - El Poblado
                            </option>
                          </select>
                        </label>

                        <label className="full-width">
                          <span>Dirección de la tienda</span>
                          <input
                            type="text"
                            name="pickupStoreAddress"
                            value={form.pickupStoreAddress}
                            onChange={updateField}
                            placeholder="Dirección de la tienda"
                          />
                        </label>
                      </div>

                      <div className="checkout-info-strip">
                        <Store size={20} />
                        <div>
                          <strong>Recogida en tienda</strong>
                          <span>
                            El costo de envío es $0. Después
                            de aprobar el pago te avisaremos
                            cuando tu pedido esté listo.
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* NOTES */}

                  <div className="checkout-card-subsection">
                    <div className="checkout-subsection-title">
                      <Package size={18} />
                      <span>INFORMACIÓN ADICIONAL</span>
                    </div>

                    <div className="checkout-form-grid">
                      <label className="full-width">
                        <span>Notas del pedido</span>
                        <textarea
                          name="notes"
                          value={form.notes}
                          onChange={updateField}
                          placeholder="Información adicional para tu pedido..."
                          rows="4"
                        />
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="checkout-primary-button"
                  >
                    CONTINUAR
                    <ArrowRight size={19} />
                  </button>
                </form>
              )}

              {/* ===========================================
                  STEP 2: PAYMENT
              =========================================== */}

              {step === 2 && (
                <section className="checkout-card">
                  <div className="checkout-card-header">
                    <div className="checkout-card-title">
                      <div className="checkout-title-icon">
                        <CreditCard size={20} />
                      </div>

                      <div>
                        <span>02 · PAGO</span>
                        <h2>
                          Selecciona tu método de pago
                        </h2>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="checkout-edit-button"
                      onClick={() => setStep(1)}
                    >
                      EDITAR DATOS
                    </button>
                  </div>

                  {/* CUSTOMER PREVIEW */}

                  <div className="checkout-customer-preview">
                    <div>
                      <strong>{form.name}</strong>
                      <span>{form.email}</span>
                      <span>{form.phone}</span>
                    </div>

                    <div>
                      <strong>
                        {deliveryType === "home"
                          ? "Domicilio"
                          : "Recoger en tienda"}
                      </strong>

                      {deliveryType === "home" ? (
                        <>
                          <span>{form.address}</span>
                          <span>
                            {form.city}, {form.department}
                          </span>
                        </>
                      ) : (
                        <>
                          <span>{form.pickupStoreName}</span>
                          {form.pickupStoreAddress && (
                            <span>{form.pickupStoreAddress}</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* PAYMENT METHODS */}

                  <div className="checkout-payment-section">
                    <span className="checkout-section-label">
                      MÉTODO DE PAGO
                    </span>

                    {loadingPaymentSettings ? (
                      <div className="checkout-payment-loading">
                        <CreditCard size={22} />
                        <span>
                          Cargando métodos de pago...
                        </span>
                      </div>
                    ) : paymentMethods.length ? (
                      <div className="checkout-payment-options">
                        {paymentMethods.map((method) => {
                          const Icon = method.icon;

                          return (
                            <label
                              key={method.value}
                              className={`checkout-payment-option ${
                                selectedPaymentMethod === method.value
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <input
                                type="radio"
                                name="paymentMethod"
                                value={method.value}
                                checked={
                                  selectedPaymentMethod === method.value
                                }
                                onChange={updateField}
                              />

                              <div className="checkout-payment-icon">
                                <Icon size={22} />
                              </div>

                              <div>
                                <strong>{method.title}</strong>
                                <span>{method.description}</span>
                              </div>

                              {selectedPaymentMethod === method.value && (
                                <CheckCircle2 size={20} />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="checkout-payment-empty">
                        <AlertCircle size={20} />
                        <span>
                          No hay métodos de pago disponibles
                          actualmente.
                        </span>
                      </div>
                    )}
                  </div>

                  {/* NEQUI DETAILS */}

                  {selectedPaymentMethod === "nequi" &&
                    paymentSettings?.nequi?.enabled && (
                      <div className="checkout-payment-data">
                        <div className="checkout-payment-data-header">
                          <Smartphone size={20} />
                          <strong>DATOS DE NEQUI</strong>
                        </div>

                        <div className="checkout-payment-data-row">
                          <span>Número</span>
                          <strong>
                            {paymentSettings.nequi.number}
                          </strong>
                        </div>

                        <div className="checkout-payment-data-row">
                          <span>Titular</span>
                          <strong>
                            {paymentSettings.nequi.holder}
                          </strong>
                        </div>
                      </div>
                    )}

                  {/* BANK DETAILS */}

                  {selectedPaymentMethod === "transfer" &&
                    paymentSettings?.bank_transfer?.enabled && (
                      <div className="checkout-payment-data">
                        <div className="checkout-payment-data-header">
                          <Building2 size={20} />
                          <strong>TRANSFERENCIA BANCARIA</strong>
                        </div>

                        <div className="checkout-payment-data-row">
                          <span>Banco</span>
                          <strong>
                            {paymentSettings.bank_transfer.bank_name}
                          </strong>
                        </div>

                        <div className="checkout-payment-data-row">
                          <span>Tipo de cuenta</span>
                          <strong>
                            {paymentSettings.bank_transfer.account_type}
                          </strong>
                        </div>

                        <div className="checkout-payment-data-row">
                          <span>Número de cuenta</span>
                          <strong>
                            {paymentSettings.bank_transfer.account_number}
                          </strong>
                        </div>

                        <div className="checkout-payment-data-row">
                          <span>Titular</span>
                          <strong>
                            {paymentSettings.bank_transfer.account_holder}
                          </strong>
                        </div>

                        {paymentSettings.bank_transfer.account_document && (
                          <div className="checkout-payment-data-row">
                            <span>Documento</span>
                            <strong>
                              {paymentSettings.bank_transfer.account_document}
                            </strong>
                          </div>
                        )}
                      </div>
                    )}

                  {/* QR */}

                  {(selectedPaymentMethod === "nequi" ||
                    selectedPaymentMethod === "transfer") &&
                    paymentSettings?.qr?.available &&
                    paymentSettings?.qr?.url && (
                      <div className="checkout-qr-box">
                        <div className="checkout-payment-data-header">
                          <QrCode size={20} />
                          <strong>PAGA CON QR</strong>
                        </div>

                        <img
                          src={getProductImage({
                            image: paymentSettings.qr.url,
                          })}
                          alt="QR de pago Debuts Crop"
                        />

                        <span>
                          Escanea el código QR desde tu
                          aplicación bancaria.
                        </span>
                      </div>
                    )}

                  {/* PAYMENT INSTRUCTIONS */}

                  {paymentSettings?.instructions && (
                    <div className="checkout-info-strip">
                      <CreditCard size={20} />
                      <div>
                        <strong>Instrucciones de pago</strong>
                        <span>{paymentSettings.instructions}</span>
                      </div>
                    </div>
                  )}

                  {/* SECURITY */}

                  <div className="checkout-security">
                    <LockKeyhole size={18} />
                    <span>
                      Tu checkout se procesa de forma segura
                      y queda asociado a tu cuenta.
                    </span>
                  </div>

                  {/* CREATE CHECKOUT */}

                  <button
                    type="button"
                    className="checkout-primary-button checkout-confirm-button"
                    onClick={createCheckout}
                    disabled={
                      placingOrder ||
                      loadingPaymentSettings ||
                      !paymentMethods.some(
                        (method) =>
                          method.value === form.paymentMethod
                      )
                    }
                  >
                    {placingOrder ? (
                      "SINCRONIZANDO CARRITO Y CREANDO CHECKOUT..."
                    ) : (
                      <>
                        CREAR CHECKOUT
                        <CheckCircle2 size={19} />
                      </>
                    )}
                  </button>
                </section>
              )}

              {/* ===========================================
                  STEP 3: PAYMENT PROOF
              =========================================== */}

              {step === 3 && (
                <section className="checkout-card">
                  <div className="checkout-card-header">
                    <div className="checkout-card-title">
                      <div className="checkout-title-icon">
                        <Upload size={20} />
                      </div>

                      <div>
                        <span>03 · COMPROBANTE</span>
                        <h2>Envía tu comprobante</h2>
                      </div>
                    </div>

                    <ShieldCheck size={22} />
                  </div>

                  {/* CHECKOUT NUMBER */}

                  <div className="checkout-proof-number">
                    <span>CHECKOUT</span>
                    <strong>{orderNumber}</strong>
                  </div>

                  {/* PAYMENT SUMMARY */}

                  <div className="checkout-payment-summary">
                    <div>
                      <span>Método</span>
                      <strong>
                        {selectedPaymentMethod === "nequi"
                          ? "Nequi"
                          : "Transferencia bancaria"}
                      </strong>
                    </div>

                    <div>
                      <span>Total</span>
                      <strong>{money(total)}</strong>
                    </div>
                  </div>

                  {/* PAYMENT REMINDER */}

                  <div className="checkout-info-strip">
                    <CreditCard size={20} />
                    <div>
                      <strong>Realiza primero el pago</strong>
                      <span>
                        Envía exactamente el valor indicado
                        en tu checkout y luego adjunta el
                        comprobante.
                      </span>
                    </div>
                  </div>

                  {/* FILE UPLOAD */}

                  <div className="checkout-proof-upload">
                    <label className="checkout-file-label">
                      <FileImage size={26} />

                      <strong>Seleccionar comprobante</strong>

                      <span>
                        JPG, PNG o WEBP · máximo 10 MB
                      </span>

                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) => {
                          const file =
                            event.target.files?.[0] || null;

                          setPaymentProof(file);
                          setErrorMessage("");
                        }}
                      />
                    </label>

                    {paymentProof && (
                      <div className="checkout-file-selected">
                        <FileImage size={19} />
                        <span>{paymentProof.name}</span>

                        <strong>
                          {(
                            paymentProof.size /
                            1024 /
                            1024
                          ).toFixed(2)}{" "}
                          MB
                        </strong>
                      </div>
                    )}
                  </div>

                  {/* UPLOAD */}

                  <button
                    type="button"
                    className="checkout-primary-button checkout-confirm-button"
                    onClick={uploadPaymentProof}
                    disabled={uploadingProof || !paymentProof}
                  >
                    {uploadingProof ? (
                      "ENVIANDO COMPROBANTE..."
                    ) : (
                      <>
                        ENVIAR COMPROBANTE
                        <Upload size={19} />
                      </>
                    )}
                  </button>
                </section>
              )}

              {/* ===========================================
                  STEP 4: PROOF RECEIVED
              =========================================== */}

              {step === 4 && checkout && (
                <section className="checkout-card">
                  <div className="checkout-card-header">
                    <div className="checkout-card-title">
                      <div className="checkout-title-icon">
                        <CheckCircle2 size={20} />
                      </div>

                      <div>
                        <span>CHECKOUT REGISTRADO</span>
                        <h2>Pago en revisión</h2>
                      </div>
                    </div>

                    <ShieldCheck size={22} />
                  </div>

                  <div className="checkout-proof-success">
                    <div className="checkout-success-icon">
                      <CheckCircle2 size={52} />
                    </div>

                    <span className="checkout-kicker">
                      DEBUTS CROP · PAYMENT
                    </span>

                    <h2>COMPROBANTE RECIBIDO</h2>

                    <p>
                      Hemos recibido tu comprobante
                      correctamente. Nuestro equipo verificará
                      el pago antes de aprobar la orden.
                    </p>

                    <div className="checkout-order-number">
                      <span>NÚMERO DE CHECKOUT</span>
                      <strong>{orderNumber}</strong>
                    </div>

                    <div className="checkout-status-box">
                      <Clock3 size={19} />
                      <span>
                        {checkout.status_display ||
                          "Pago en revisión"}
                      </span>
                    </div>
                  </div>

                  <div className="checkout-info-strip">
                    <ShieldCheck size={20} />
                    <div>
                      <strong>¿Qué sigue?</strong>
                      <span>
                        Cuando el pago sea aprobado, se
                        generará automáticamente tu PIN de
                        verificación.
                      </span>
                    </div>
                  </div>

                  <div className="checkout-success-actions">
                    <button
                      type="button"
                      onClick={() => navigate("/profile")}
                    >
                      VER MI PEDIDO
                      <ArrowRight size={18} />
                    </button>

                    <button
                      type="button"
                      className="secondary"
                      onClick={() => navigate("/catalog")}
                    >
                      SEGUIR COMPRANDO
                    </button>
                  </div>
                </section>
              )}
            </section>

            {/* =============================================
                ORDER SUMMARY
            ============================================= */}

            <aside className="checkout-summary">
              <div className="checkout-summary-card">
                <div className="checkout-summary-header">
                  <div>
                    <span>DEBUTS CROP</span>
                    <h2>Tu pedido</h2>
                  </div>

                  <ShoppingCart size={22} />
                </div>

                <div className="checkout-summary-count">
                  {totalItems}{" "}
                  {totalItems === 1 ? "producto" : "productos"}
                </div>

                <div className="checkout-products">
                  {cart.length ? (
                    cart.map((item, index) => {
                      const image = getProductImage(item);
                      const quantity = getQuantity(item);
                      const price = getPrice(item);
                      const productId = getProductId(item);

                      return (
                        <div
                          className="checkout-product"
                          key={
                            productId !== undefined &&
                            productId !== null
                              ? String(productId)
                              : `checkout-item-${index}`
                          }
                        >
                          <div className="checkout-product-image">
                            {image ? (
                              <img
                                src={image}
                                alt={getProductName(item)}
                              />
                            ) : (
                              <Package size={27} />
                            )}
                          </div>

                          <div className="checkout-product-info">
                            <strong>{getProductName(item)}</strong>

                            <span>
                              {money(price)} · {quantity} ud.
                            </span>

                            <div className="checkout-quantity">
                              <button
                                type="button"
                                onClick={() =>
                                  changeQuantity(item, -1)
                                }
                                aria-label="Disminuir cantidad"
                                disabled={Boolean(checkout)}
                              >
                                <Minus size={14} />
                              </button>

                              <span>{quantity}</span>

                              <button
                                type="button"
                                onClick={() =>
                                  changeQuantity(item, 1)
                                }
                                aria-label="Aumentar cantidad"
                                disabled={Boolean(checkout)}
                              >
                                <Plus size={14} />
                              </button>

                              <button
                                type="button"
                                className="checkout-remove"
                                onClick={() => removeItem(item)}
                                aria-label="Eliminar producto"
                                disabled={Boolean(checkout)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          <strong className="checkout-product-total">
                            {money(price * quantity)}
                          </strong>
                        </div>
                      );
                    })
                  ) : (
                    <div className="checkout-empty-products">
                      <Package size={30} />
                      <span>Checkout creado</span>
                    </div>
                  )}
                </div>

                {/* SUMMARY LINES */}

                <div className="checkout-summary-lines">
                  <div>
                    <span>Subtotal</span>
                    <strong>{money(backendSubtotal)}</strong>
                  </div>

                  <div>
                    <span>Envío</span>
                    <strong>
                      {shipping ? money(shipping) : "GRATIS"}
                    </strong>
                  </div>

                  {Number(checkout?.discount ?? 0) > 0 && (
                    <div>
                      <span>Descuento</span>
                      <strong>
                        -{money(checkout.discount)}
                      </strong>
                    </div>
                  )}
                </div>

                {/* TOTAL */}

                <div className="checkout-total">
                  <span>TOTAL</span>
                  <strong>{money(total)}</strong>
                </div>

                {/* DELIVERY + SECURITY */}

                <div className="checkout-trust">
                  <div>
                    {deliveryType === "home" ? (
                      <Truck size={17} />
                    ) : (
                      <Store size={17} />
                    )}

                    {deliveryType === "home"
                      ? "Domicilio"
                      : "Recoger en tienda"}
                  </div>

                  <div>
                    <ShieldCheck size={17} />
                    Compra segura
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
